'use client'
import * as Dialog from '@radix-ui/react-dialog'
import { motion, type Variants } from 'framer-motion'
import { X } from 'lucide-react'
import Image, { type StaticImageData } from 'next/image'
import { useRef } from 'react'

export type TeacherItem = {
  id: number
  photo: string | StaticImageData
  name: string
  introHtml: string
  qualificationsHtml: string
  experienceHtml: string
}

export type TeacherLabels = {
  role: string
  viewProfile: string
  about: string
  qualifications: string
  experience: string
  close: string
}

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
}
const cardVariants: Variants = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 120, damping: 18, mass: 0.7 } },
}

const ProfileBlock = ({ title, html }: { title: string; html: string }) =>
  html ? (
    <section className='mt-6 first:mt-0'>
      <h4 className='mb-2 text-sm font-semibold uppercase tracking-[0.15em] text-tertiary-lightFonts'>{title}</h4>
      <div
        className='prose prose-invert max-w-none prose-a:text-tertiary-lightFonts prose-a:underline'
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </section>
  ) : null

const TeachersGrid = ({ teachers, labels }: { teachers: TeacherItem[]; labels: TeacherLabels }) => {
  const ref = useRef(null)

  return (
    <motion.ul
      ref={ref}
      variants={container}
      initial='hidden'
      whileInView='show'
      viewport={{ once: true, amount: 0.15 }}
      className='grid grid-cols-[minmax(0,20rem)] justify-center gap-x-10 gap-y-14 sm:grid-cols-[repeat(auto-fit,20rem)]'
    >
      {teachers.map(teacher => {
        const hasBio = Boolean(teacher.introHtml || teacher.qualificationsHtml || teacher.experienceHtml)
        return (
          <motion.li key={teacher.id} variants={cardVariants} className='group'>
            <figure className='relative mx-auto aspect-[4/5] w-full max-w-[20rem] overflow-hidden rounded-[28px] shadow-lg ring-1 ring-white/10'>
              <Image
                fill
                src={teacher.photo}
                alt={teacher.name}
                loading='lazy'
                sizes='(max-width: 640px) 100vw, 20rem'
                className='object-cover transition-transform duration-500 group-hover:scale-105'
              />
              <div className='absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent' />
              <figcaption className='absolute inset-x-0 bottom-0 space-y-2 p-6 text-center'>
                <p className='text-xs font-semibold uppercase tracking-[0.2em] text-tertiary-lightFonts'>
                  {labels.role}
                </p>
                <h3 className='text-xl font-semibold text-white'>{teacher.name}</h3>
                {hasBio ? (
                  <Dialog.Root>
                    <Dialog.Trigger asChild>
                      <button
                        type='button'
                        className='mt-2 inline-flex rounded-full bg-tertiary-lightFonts px-5 py-1.5 text-sm font-semibold text-tertiary-dark transition-transform duration-200 hover:scale-105'
                      >
                        {labels.viewProfile}
                      </button>
                    </Dialog.Trigger>
                    <Dialog.Portal>
                      <Dialog.Overlay className='fixed inset-0 z-50 bg-black/70 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in' />
                      <Dialog.Content
                        aria-describedby={undefined}
                        className='fixed left-1/2 top-1/2 z-50 max-h-[85vh] w-[92vw] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-3xl bg-tertiary-dark p-8 text-white shadow-2xl'
                      >
                        <div className='mb-6 flex items-center gap-4 text-start'>
                          <div className='relative size-16 shrink-0 overflow-hidden rounded-full ring-2 ring-tertiary-lightFonts/40'>
                            <Image fill src={teacher.photo} alt={teacher.name} sizes='64px' className='object-cover' />
                          </div>
                          <div>
                            <p className='text-xs font-semibold uppercase tracking-[0.2em] text-tertiary-lightFonts'>
                              {labels.role}
                            </p>
                            <Dialog.Title className='text-2xl font-bold text-white'>{teacher.name}</Dialog.Title>
                          </div>
                        </div>
                        <div className='text-start'>
                          <ProfileBlock title={labels.about} html={teacher.introHtml} />
                          <ProfileBlock title={labels.qualifications} html={teacher.qualificationsHtml} />
                          <ProfileBlock title={labels.experience} html={teacher.experienceHtml} />
                        </div>
                        <Dialog.Close
                          className='absolute end-5 top-5 flex size-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20'
                          aria-label={labels.close}
                        >
                          <X className='size-5' />
                        </Dialog.Close>
                      </Dialog.Content>
                    </Dialog.Portal>
                  </Dialog.Root>
                ) : null}
              </figcaption>
            </figure>
          </motion.li>
        )
      })}
    </motion.ul>
  )
}

export default TeachersGrid
